import React, { useEffect, useState } from 'react';
import { useAdmin } from '../../context/AdminContext';
import { adminListOrders, adminUpdateOrder } from '../../services/toolsApi';
import { chargeCardToken } from '../../services/monobankPayment';

const STATUS_COLORS = {
  pending: 'bg-yellow-100 text-yellow-700',
  paid: 'bg-green-100 text-green-700',
  dispensed: 'bg-blue-100 text-blue-700',
  conflict: 'bg-red-100 text-red-700',
  refunded: 'bg-gray-100 text-gray-600',
  returned: 'bg-gray-100 text-gray-600',
};

const STATUS_PRIORITY = { conflict: 4, pending: 3, dispensed: 2, paid: 1, refunded: 0, returned: 0 };
const CLOSED_STATUSES = ['returned', 'refunded'];

const groupByReference = (orders) => {
  const map = new Map();
  for (const order of orders) {
    if (!map.has(order.reference)) {
      map.set(order.reference, {
        reference: order.reference,
        ids: [],
        vendingMachineId: order.vendingMachineId,
        status: order.status,
        createdAt: order.createdAt,
        customerFirstName: order.customerFirstName,
        customerLastName: order.customerLastName,
        customerMiddleName: order.customerMiddleName,
        customerPhone: order.customerPhone,
        diiaVerified: order.diiaVerified,
        hasCardToken: !!order.hasCardToken,
        cardMaskedPan: order.cardMaskedPan || null,
        items: [],
        totalAmount: 0,
      });
    }
    const group = map.get(order.reference);
    group.ids.push(order.id);
    group.items.push({
      id: order.id,
      toolId: order.toolId,
      toolName: order.toolName,
      rentalType: order.rentalType,
      duration: order.duration,
      amount: order.amount,
      status: order.status,
      returnedAt: order.returnedAt ?? null,
    });
    group.totalAmount += order.amount;
    if ((STATUS_PRIORITY[order.status] ?? 0) > (STATUS_PRIORITY[group.status] ?? 0)) {
      group.status = order.status;
    }
  }
  return Array.from(map.values());
};

const CONFIRM_LABELS = {
  paid: 'Mark this order as Paid?',
  returned: 'Mark this order as Returned?',
  refunded: 'Confirm the customer was refunded? Do this only after the refund went through in Monobank.',
};

const OrderModal = ({ order, onClose, onStatusChange, password }) => {
  const [confirm, setConfirm] = useState(null);
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeComment, setChargeComment] = useState('');
  const [charging, setCharging] = useState(false);
  const [chargeResult, setChargeResult] = useState(null); // { success, error }

  const handleConfirm = async () => {
    await onStatusChange(order.ids, confirm);
    setConfirm(null);
    onClose();
  };

  const handleCharge = async () => {
    const amount = parseFloat(chargeAmount);
    if (!amount || amount <= 0) return;
    setCharging(true);
    setChargeResult(null);
    try {
      await chargeCardToken({
        orderId: order.ids[0],
        amount,
        comment: chargeComment || `Штраф: ${order.reference}`,
        password,
      });
      setChargeResult({ success: true });
      setChargeAmount('');
      setChargeComment('');
    } catch (e) {
      setChargeResult({ success: false, error: e.message });
    } finally {
      setCharging(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl w-full max-w-md shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <span className={`text-sm px-2.5 py-0.5 rounded-lg font-semibold ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>
              {order.status}
            </span>
            {order.diiaVerified && (
              <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-lg">
                ✓ Diia
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-black text-xl font-bold bg-transparent border-none cursor-pointer leading-none">✕</button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* Items */}
          <div>
            <div className="text-xs text-gray-400 uppercase font-semibold mb-2">
              Tools ({order.items.length})
            </div>
            <div className="space-y-2">
              {order.items.map((item, i) => (
                <div key={i} className={`rounded-xl px-3 py-2.5 ${item.status === 'returned' ? 'bg-gray-50 border border-gray-200' : 'bg-yellow-50 border border-yellow-100'}`}>
                  <div className="flex items-center justify-between gap-2 mb-0.5">
                    <div className="text-sm font-bold text-black">{item.toolName || item.toolId}</div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-lg flex-shrink-0 ${item.status === 'returned' ? 'bg-gray-200 text-gray-600' : 'bg-yellow-200 text-yellow-800'}`}>
                      {item.status === 'returned' ? 'returned' : 'not returned'}
                    </span>
                  </div>
                  <div className="text-xs text-gray-500">
                    {item.rentalType} &middot; {item.duration} unit(s) &middot; <span className="font-semibold text-black">{item.amount} UAH</span>
                  </div>
                  {item.status === 'returned' && item.returnedAt && (
                    <div className="text-xs text-gray-400 mt-1">
                      Returned {new Date(item.returnedAt).toLocaleString('uk-UA', { timeZone: 'Europe/Kyiv' })}
                      {(order.customerLastName || order.customerFirstName) && (
                        <span> by {[order.customerLastName, order.customerFirstName].filter(Boolean).join(' ')}</span>
                      )}
                      {order.customerPhone && !order.customerLastName && !order.customerFirstName && (
                        <span> by {order.customerPhone}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {order.items.length > 1 && (
              <div className="text-sm font-bold text-black mt-2 text-right">
                Total: {order.totalAmount} UAH
              </div>
            )}
          </div>

          {(order.customerFirstName || order.customerLastName) && (
            <div>
              <div className="text-xs text-gray-400 uppercase font-semibold mb-0.5">Customer</div>
              <div className="text-base font-medium text-blue-700">
                {[order.customerLastName, order.customerFirstName, order.customerMiddleName].filter(Boolean).join(' ')}
              </div>
              {order.customerPhone && <div className="text-sm text-gray-500">{order.customerPhone}</div>}
            </div>
          )}

          <div>
            <div className="text-xs text-gray-400 uppercase font-semibold mb-0.5">Machine</div>
            <div className="text-sm text-gray-700">{order.vendingMachineId}</div>
          </div>

          <div>
            <div className="text-xs text-gray-400 uppercase font-semibold mb-0.5">Date</div>
            <div className="text-sm text-gray-700">{new Date(order.createdAt).toLocaleString('uk-UA', { timeZone: 'Europe/Kyiv' })}</div>
          </div>

          <div>
            <div className="text-xs text-gray-400 uppercase font-semibold mb-0.5">Reference</div>
            <div className="text-xs text-gray-500 break-all">{order.reference}</div>
          </div>

          {/* Card token section */}
          <div className={`rounded-xl p-3 ${order.hasCardToken ? 'bg-blue-50 border border-blue-100' : 'bg-gray-50 border border-gray-100'}`}>
            <div className="text-xs text-gray-400 uppercase font-semibold mb-1.5">Карта / Токен</div>
            {order.hasCardToken ? (
              <>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-bold text-gray-800">{order.cardMaskedPan || '••••'}</span>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-lg font-semibold">Токен є</span>
                </div>
                {chargeResult?.success && (
                  <div className="text-xs text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2 mb-2 font-semibold">
                    Списання успішно відправлено
                  </div>
                )}
                {chargeResult?.error && (
                  <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2">
                    {chargeResult.error}
                  </div>
                )}
                <div className="flex gap-2 mb-2">
                  <input
                    type="number"
                    min="1"
                    placeholder="Сума, грн"
                    value={chargeAmount}
                    onChange={e => setChargeAmount(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border-2 border-gray-200 rounded-xl outline-none focus:border-blue-400"
                  />
                  <input
                    type="text"
                    placeholder="Коментар (необов.)"
                    value={chargeComment}
                    onChange={e => setChargeComment(e.target.value)}
                    className="flex-1 px-3 py-2 text-sm border-2 border-gray-200 rounded-xl outline-none focus:border-blue-400"
                  />
                </div>
                <button
                  onClick={handleCharge}
                  disabled={charging || !chargeAmount}
                  className="w-full px-4 py-2.5 text-sm bg-red-500 text-white rounded-xl border-none cursor-pointer hover:bg-red-600 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {charging ? 'Списуємо...' : `Списати${chargeAmount ? ` ${chargeAmount} грн` : ''}`}
                </button>
              </>
            ) : (
              <div className="text-sm text-gray-400">Токен не збережено</div>
            )}
          </div>
        </div>

        {/* Actions */}
        {order.status === 'conflict' && !confirm && (
          <div className="px-5 pb-5">
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-3 text-sm text-red-700">
              Paid, but the tool was already taken. No pickup code was issued — refund the customer in Monobank.
            </div>
            <button
              onClick={() => setConfirm('refunded')}
              className="w-full px-4 py-2.5 text-sm bg-gray-100 text-gray-700 rounded-xl border-none cursor-pointer hover:bg-gray-200 font-semibold"
            >
              Mark Refunded
            </button>
          </div>
        )}

        {!CLOSED_STATUSES.includes(order.status) && order.status !== 'conflict' && !confirm && (
          <div className="flex gap-2 px-5 pb-5">
            {order.status === 'pending' && (
              <button
                onClick={() => setConfirm('paid')}
                className="flex-1 px-4 py-2.5 text-sm bg-green-50 text-green-700 rounded-xl border-none cursor-pointer hover:bg-green-100 font-semibold"
              >
                Mark Paid
              </button>
            )}
            <button
              onClick={() => setConfirm('returned')}
              className="flex-1 px-4 py-2.5 text-sm bg-gray-100 text-gray-700 rounded-xl border-none cursor-pointer hover:bg-gray-200 font-semibold"
            >
              Mark Returned
            </button>
          </div>
        )}

        {/* Confirmation */}
        {confirm && (
          <div className="px-5 pb-5">
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 mb-3 text-sm text-yellow-800 font-medium text-center">
              {CONFIRM_LABELS[confirm]}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirm(null)}
                className="flex-1 px-4 py-2.5 text-sm bg-gray-100 text-gray-700 rounded-xl border-none cursor-pointer hover:bg-gray-200 font-semibold"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-2.5 text-sm bg-dewalt text-black rounded-xl border-none cursor-pointer hover:bg-dewalt-hover font-semibold"
              >
                Confirm
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const AdminOrders = () => {
  const { password } = useAdmin();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await adminListOrders(password);
      setOrders(groupByReference(data));
    } catch (err) {
      setLoadError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStatusChange = async (ids, newStatus) => {
    try {
      await Promise.all(ids.map(id => adminUpdateOrder(password, id, newStatus)));
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const filtered = filter === 'all' ? orders : orders.filter(o => o.status === filter);

  if (loading) {
    return <div className="flex justify-center py-12"><div className="w-8 h-8 border-3 border-dewalt border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-xl font-bold text-black">Orders</h1>
        <div className="flex gap-2 flex-wrap justify-end">
          {['all', 'conflict', 'pending', 'dispensed', 'paid', 'returned'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-sm rounded-lg border-none cursor-pointer transition-colors ${
                filter === f ? 'bg-dewalt text-black font-bold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
              {f !== 'all' && ` (${orders.filter(o => o.status === f).length})`}
            </button>
          ))}
        </div>
      </div>

      {loadError ? (
        <div className="text-center py-8">
          <p className="text-red-600 mb-3">Could not load orders: {loadError}</p>
          <button onClick={load} className="px-4 py-2 text-sm bg-dewalt rounded-lg border-none cursor-pointer font-semibold">
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <p className="text-gray-500 text-center py-8">No orders found</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => (
            <div
              key={order.reference}
              className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-dewalt transition-colors"
              onClick={() => setSelectedOrder(order)}
            >
              <div className="flex items-start justify-between mb-3 gap-2">
                <div className="flex items-center gap-3">
                  <span className="text-base font-bold text-black">#{order.ids[0]}</span>
                  <span className={`text-sm px-2.5 py-0.5 rounded-lg font-semibold ${STATUS_COLORS[order.status] || 'bg-gray-100 text-gray-600'}`}>
                    {order.status}
                  </span>
                </div>
                <span className="text-sm font-semibold text-gray-700">{new Date(order.createdAt).toLocaleString('uk-UA', { timeZone: 'Europe/Kyiv' })}</span>
              </div>
              <div className="mb-2">
                {order.items.map((item, i) => (
                  <div key={i} className="text-base font-bold text-black leading-snug">{item.toolName || item.toolId}</div>
                ))}
              </div>
              {(order.customerFirstName || order.customerLastName || order.customerPhone) && (
                <div className="text-sm text-blue-700 mb-1 flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{[order.customerLastName, order.customerFirstName, order.customerMiddleName].filter(Boolean).join(' ')}</span>
                  {order.customerPhone && <span className="text-gray-500">{order.customerPhone}</span>}
                  {order.diiaVerified && (
                    <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-semibold px-2 py-0.5 rounded-lg">
                      ✓ Diia
                    </span>
                  )}
                </div>
              )}
              <div className="text-sm text-gray-500 mb-1">
                {order.items.length} item(s) &middot; <span className="font-semibold text-black">{order.totalAmount} UAH</span> &middot; {order.vendingMachineId}
              </div>
              <div className="text-xs text-gray-400 break-all">Ref: {order.reference}</div>
            </div>
          ))}
        </div>
      )}

      {selectedOrder && (
        <OrderModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onStatusChange={handleStatusChange}
          password={password}
        />
      )}
    </div>
  );
};

export default AdminOrders;
